import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Check, ChevronDown, AlertCircle } from 'lucide-react';
import { TAX_CODES, TaxCode } from '../types/invoice';
interface TaxCodeSelectorProps {
  selectedCode?: string;
  onChange: (code: string) => void;
  hasError?: boolean;
  errorMessage?: string;
}
export function TaxCodeSelector({
  selectedCode,
  onChange,
  hasError = false,
  errorMessage
}: TaxCodeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(selectedCode || '');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<'below' | 'above'>('below');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Sync input value with selected code
  useEffect(() => {
    setInputValue(selectedCode || '');
  }, [selectedCode]);
  // Filter tax codes based on input - but show all when input matches selected code
  const filteredCodes = useMemo(() => {
    // If input is empty or matches the selected code exactly, show all options
    if (!inputValue || inputValue === selectedCode) {
      return TAX_CODES;
    }
    // Otherwise filter based on search
    const searchText = inputValue.toLowerCase();
    return TAX_CODES.filter(taxCode => taxCode.code.toLowerCase().includes(searchText) || taxCode.meaning.toLowerCase().includes(searchText) || taxCode.gstRate.toLowerCase().includes(searchText));
  }, [inputValue, selectedCode]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setInputValue(value);
    setIsOpen(true);
    setHighlightedIndex(0);
    // Auto-select if exact match
    const exactMatch = TAX_CODES.find(tc => tc.code === value);
    if (exactMatch) {
      onChange(exactMatch.code);
    }
  };
  const handleInputFocus = () => {
    setIsOpen(true);
    // Calculate if dropdown should open above or below
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 500; // max-h-[500px]
      // If not enough space below but more space above, open above
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownPosition('above');
      } else {
        setDropdownPosition('below');
      }
    }
    // When opening, highlight the currently selected option
    if (selectedCode) {
      const selectedIndex = filteredCodes.findIndex(tc => tc.code === selectedCode);
      setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    } else {
      setHighlightedIndex(0);
    }
  };
  const handleSelectCode = (code: string) => {
    setInputValue(code);
    onChange(code);
    setIsOpen(false);
    inputRef.current?.blur();
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setIsOpen(true);
      return;
    }
    if (!isOpen) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => prev < filteredCodes.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredCodes[highlightedIndex]) {
          handleSelectCode(filteredCodes[highlightedIndex].code);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };
  // Scroll highlighted option into view
  useEffect(() => {
    if (isOpen && dropdownRef.current && filteredCodes.length > 0 && highlightedIndex >= 0) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest'
        });
      }
    }
  }, [highlightedIndex, isOpen, filteredCodes.length]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedTaxCode = TAX_CODES.find(tc => tc.code === selectedCode);
  return <div ref={containerRef} className="relative" data-id="element-7170">
      <div className="relative" data-id="element-7171">
        <input ref={inputRef} type="text" value={inputValue} onChange={handleInputChange} onFocus={handleInputFocus} onKeyDown={handleKeyDown} placeholder="Type code..." maxLength={1} className={`w-full px-3 py-2 pr-8 rounded bg-white text-sm font-bold uppercase
                   focus:outline-none transition-colors ${hasError ? 'border-2 border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500' : 'border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400'}`} data-id="element-7172" />
        <button type="button" onClick={() => {
        if (isOpen) {
          setIsOpen(false);
        } else {
          handleInputFocus();
        }
      }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-7173">
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-7174" />
        </button>
      </div>

      {/* Selected code info - shown when not open */}
      {selectedTaxCode && !isOpen && <div className="mt-1 text-xs text-gray-600" data-id="element-7175">
          {selectedTaxCode.gstRate} GST
        </div>}

      {/* Dropdown - positioned fixed to avoid overflow issues */}
      {isOpen && <div className="fixed inset-0 z-[9999]" onClick={() => setIsOpen(false)} data-id="element-7176">
          <div onClick={e => e.stopPropagation()} style={{
        position: 'fixed',
        ...(dropdownPosition === 'below' ? {
          top: containerRef.current?.getBoundingClientRect().bottom ?? 0
        } : {
          bottom: window.innerHeight - (containerRef.current?.getBoundingClientRect().top ?? 0)
        }),
        left: containerRef.current?.getBoundingClientRect().left ?? 0,
        width: Math.max(containerRef.current?.getBoundingClientRect().width ?? 0, 420)
      }} className="bg-white border border-gray-300 rounded-lg shadow-xl max-h-[500px] overflow-auto animate-fade-in" data-id="element-7177">
            {filteredCodes.length > 0 ? <div ref={dropdownRef} data-id="element-7178">
                {filteredCodes.map((taxCode, index) => {
            const isSelected = taxCode.code === selectedCode;
            const isHighlighted = index === highlightedIndex;
            return <button key={taxCode.code} type="button" onClick={() => handleSelectCode(taxCode.code)} onMouseEnter={() => setHighlightedIndex(index)} className={`w-full px-4 py-3 text-left transition-colors border-b border-gray-100 last:border-b-0 ${isSelected ? 'bg-blue-100 hover:bg-blue-200' : isHighlighted ? 'bg-blue-50' : 'hover:bg-blue-50'}`} data-id="element-7179">
                      <div className="flex items-start justify-between gap-3" data-id="element-7180">
                        <div className="flex-1 min-w-0" data-id="element-7181">
                          <div className="flex items-center gap-2 mb-1.5" data-id="element-7182">
                            <span className={`font-bold text-xl ${isSelected ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-7183">
                              {taxCode.code}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded ${isSelected ? 'bg-blue-200 text-blue-800' : 'bg-blue-100 text-blue-700'}`} data-id="element-7184">
                              {taxCode.gstRate} GST
                            </span>
                          </div>
                          <div className={`text-sm font-medium mb-2 ${isSelected ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-7185">
                            {taxCode.meaning}
                          </div>
                          <div className="text-xs text-gray-600 space-y-1" data-id="element-7186">
                            <div className="flex items-start gap-1.5" data-id="element-7187">
                              <span className="font-semibold text-gray-700 min-w-[90px]" data-id="element-7188">
                                BAS Equivalent:
                              </span>
                              <span data-id="element-7189">{taxCode.basEquivalent}</span>
                            </div>
                            <div className="flex items-start gap-1.5" data-id="element-7190">
                              <span className="font-semibold text-gray-700 min-w-[90px]" data-id="element-7191">
                                Treatment:
                              </span>
                              <span className="text-gray-500" data-id="element-7192">
                                {taxCode.basTreatment}
                              </span>
                            </div>
                          </div>
                        </div>
                        {isSelected && <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" data-id="element-7193" />}
                      </div>
                    </button>;
          })}
              </div> : <div className="px-4 py-6 text-center" data-id="element-7194">
                <p className="text-sm text-gray-500" data-id="element-7195">
                  No tax codes match "{inputValue}"
                </p>
                <p className="text-xs text-gray-400 mt-1" data-id="element-7196">
                  Valid codes: I, E, Z, R, N
                </p>
              </div>}
          </div>
        </div>}
    </div>;
}